import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Chip, Button, CardActions, IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/user';

/**
 * Requirement interface
 */
interface Requirement {
  id: string | number;
  name: string;
  description?: string;
  status: 'completed' | 'in-progress' | 'not-started' | 'archived';
  controlId: string | number;
  controlName?: string;
  obligationId?: string | number;
  obligationName?: string;
  frameworkId?: string | number;
  frameworkName?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Props interface
 */
interface RequirementCardProps {
  requirement: Requirement;
}

/**
 * Requirement Card Component
 * 
 * Displays a single requirement in a card view
 * Used by RequirementList component
 */
export default function RequirementCard({ requirement }: RequirementCardProps) {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.REQUIREMENT_EDIT);
  const canDelete = hasPermission(Permission.REQUIREMENT_DELETE);
  /**
   * Get status chip color based on status
   */
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'not-started':
        return 'warning';
      case 'archived':
        return 'error';
      default:
        return 'default';
    }
  };

  /**
   * Get status label
   */
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'In Progress';
      case 'not-started':
        return 'Not Started';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  /**
   * Get formatted date
   */
  const getFormattedDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  /**
   * Handle delete click
   */
  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete "${requirement.name}"? This action cannot be undone.`)) {
      try {
        const response = await fetch(`/api/requirements/${requirement.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          // Show success message
          alert('Requirement deleted successfully');
          // Reload the page to refresh the list
          window.location.reload();
        } else {
          const error = await response.json();
          throw new Error(error.message || 'Failed to delete requirement');
        }
      } catch (error) {
        console.error('Error deleting requirement:', error);
        alert(`Error deleting requirement: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  /**
   * Render component
   */
  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="div" gutterBottom data-testid="requirement-title">
            {requirement.name}
          </Typography>
          <Chip
            label={getStatusLabel(requirement.status)}
            size="small"
            color={getStatusColor(requirement.status) as any}
          />
        </Box>
        
        <Box mb={1}>
          {requirement.controlName && (
            <Typography variant="caption" color="text.secondary" display="block">
              Control: {requirement.controlName}
            </Typography>
          )}
          {requirement.obligationName && (
            <Typography variant="caption" color="text.secondary" display="block">
              Obligation: {requirement.obligationName}
            </Typography>
          )}
          {requirement.frameworkName && (
            <Typography variant="caption" color="text.secondary" display="block">
              Framework: {requirement.frameworkName}
            </Typography>
          )}
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {requirement.description || 'No description available'}
        </Typography>
        
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Updated {getFormattedDate(requirement.updatedAt)}
          </Typography>
        </Box>
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'space-between' }}>
        <Button
          size="small"
          component={Link}
          to={`/requirements/${requirement.id}`}
          data-testid="view-requirement-button"
        >
          View Details
        </Button>
        <Box>
          {canEdit && (
            <IconButton
              size="small"
              component={Link}
              to={`/requirements/${requirement.id}/edit`}
              data-testid={`edit-requirement-${requirement.id}`}
              aria-label="Edit requirement"
            >
              <EditIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={handleDelete}
              data-testid={`delete-requirement-${requirement.id}`}
              aria-label="Delete requirement"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </CardActions>
    </Card>
  );
}
